import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection, creationType, SpeakerRoles, SubjectStatus } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'utils/notify'
import { chunk } from 'lodash'
import moment from 'moment-timezone'
import { nanoid } from 'nanoid'
import { cloudFunctionsInstance } from 'config/axios'
import { placeImageToDifferentPath } from '../utils/subject'
import { createOrUpdateAttendeeForSpeaker } from 'store/attendee'

const DEFAULT_STATUS_FILTER = 'all'
const DEFAULT_SORT_ORDER = { name: '', direction: 'asc' }
export const initialState = {
  error: null,
  inProgress: false,
  statusFilter: DEFAULT_STATUS_FILTER,
  speakers: [],
  speaker: null,
  duplicateSpeakers: [],
  currentPage: 0,
  pageSize: 10,
  sortOrder: DEFAULT_SORT_ORDER,
  searchText: ''
}

const speakerSlice = createSlice({
  name: 'speaker',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload
    },
    setSpeakers: (state, action) => {
      state.speakers = action.payload
    },
    setSpeaker: (state, action) => {
      state.speaker = action.payload
    },
    setDuplicateSpeakers: (state, action) => {
      state.duplicateSpeakers = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.statusFilter = DEFAULT_STATUS_FILTER
      state.error = null
      state.inProgress = false
      state.speakers = []
      state.speakers = null
      state.duplicateSpeakers = []
      state.currentPage = 0
      state.pageSize = 10
      state.sortOrder = DEFAULT_SORT_ORDER
      state.searchText = ''
    }
  }
})

export const addSpeaker = (payload, conferenceId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const { email } = payload
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    if (snapshot?.empty) {
      const doc = await FirestoreDatabase.collection(Collection.SPEAKERS).add({
        ...payload,
        createdAt: moment().utc().valueOf(),
        token: nanoid(),
        status: SubjectStatus.APPROVED,
        creationType: creationType.MANUAL,
        conferenceId: conferenceId
      })

      const attendeeId = await createOrUpdateAttendeeForSpeaker(
        { ...payload, speakerId: doc.id },
        conferenceId
      )
      if (attendeeId) {
        await FirestoreDatabase.collection(Collection.SPEAKERS).doc(doc.id).update({ attendeeId })
      }

      return doc.id
    } else {
      toast.error('A speaker with this email already exists.')
      return null
    }
  } catch (err) {
    toast.error('Could not add speaker.')
    console.error(err)
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const createAndInviteSpeaker = async (payload, conferenceId) => {
  try {
    const { email } = payload
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    if (snapshot.empty) {
      const doc = await FirestoreDatabase.collection(Collection.SPEAKERS).add({
        ...payload,
        fullName: `${payload.firstName} ${payload.lastName}`,
        createdAt: moment().utc().valueOf(),
        token: nanoid(),
        conferenceId: conferenceId,
        creationType: creationType.INVITATION,
        status: SubjectStatus.INVITE_NOT_SENT
      })

      const attendeeId = await createOrUpdateAttendeeForSpeaker(
        { ...payload, speakerId: doc.id },
        conferenceId
      )
      if (attendeeId) {
        await FirestoreDatabase.collection(Collection.SPEAKERS).doc(doc.id).update({ attendeeId })
      }

      try {
        await cloudFunctionsInstance.post('/inviteOrRemindSpeakersViaEmail', {
          speakerIds: [doc.id],
          conferenceId
        })
        toast.success('Speaker created and invitation sent.')
      } catch (err) {
        const errorMessage =
          err?.response?.data?.msg || 'Failed to send email, Please try again later'
        toast.error(errorMessage)
      }
    } else {
      toast.error('A speaker with this email already exists.')
      return null
    }
  } catch (err) {
    toast.error('Could not create or invite speaker.')
    console.error(err)
  }
}

export const scanImportedSpeakers = async (importedSpeakers, conferenceId) => {
  // Get the current conference to check if it's linked to a previous conference
  const confSnap = await FirestoreDatabase.collection(Collection.CONFERENCES)
    .doc(conferenceId)
    .get()
  const conference = { id: confSnap.id, ...confSnap.data() }
  const linkedConferenceId = conference?.previousConferenceId || ''

  if (linkedConferenceId) {
    // Get speakers from the linked conference
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('conferenceId', '==', linkedConferenceId)
      .get()
    const confSpeakers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

    const brandNewSpeakers = []
    const existingSpeakers = []

    // Check each imported speaker if it exists in the previous conference
    importedSpeakers.forEach((obj) => {
      const email = obj.email
      let found = false

      for (let i = 0; i < confSpeakers.length; i++) {
        if (confSpeakers[i].email === email) {
          found = true
          const existingSpeaker = { ...confSpeakers[i] }
          const speakerPayload = {
            ...obj,
            firstName: obj?.firstName || existingSpeaker?.firstName || '',
            lastName: obj?.lastName || existingSpeaker?.lastName || '',
            fullName: obj?.fullName || existingSpeaker?.fullName || '',
            email: obj?.email || existingSpeaker?.email || '',

            credential: obj?.credential || existingSpeaker?.credential || '',
            position: obj?.position || existingSpeaker?.position || '',
            company: obj?.company || existingSpeaker?.company || '',
            phone: obj?.phone || existingSpeaker?.phone || '',
            city: obj?.city || existingSpeaker?.city || '',
            state: obj?.state || existingSpeaker?.state || '',
            country: obj?.country || existingSpeaker?.country || '',

            websiteUrl: obj?.websiteUrl || existingSpeaker?.websiteUrl || '',
            bio: obj?.bio || existingSpeaker?.bio || '',
            roles: obj?.roles || existingSpeaker?.roles || [SpeakerRoles.PRESENTER],
            imageName: existingSpeaker.imageName || null,
            token: obj?.token || nanoid(),
            status: obj?.status || SubjectStatus.INVITE_NOT_SENT,
            conferenceId: obj?.conferenceId || conferenceId,
            updateInfoNotificationSent: false
          }

          existingSpeakers.push(speakerPayload)
          break
        }
      }

      if (!found) {
        brandNewSpeakers.push(obj)
      }
    })

    return {
      importedSpeakers: importedSpeakers,
      brandNewSpeakers: brandNewSpeakers,
      existingSpeakers: existingSpeakers
    }
  }

  // If no previous conference is linked, all are brand new
  return {
    importedSpeakers: importedSpeakers,
    brandNewSpeakers: importedSpeakers,
    existingSpeakers: []
  }
}

export const bulkInsertSpeakers = async (importedSpeakers, conferenceId, previousConferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const speakersData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    const existingSpeakerEmails = speakersData.map((speaker) => speaker.email)

    const uniqueSpeakers = importedSpeakers
      .filter((e) => !existingSpeakerEmails.includes(e.email))
      .map((s) => ({ id: nanoid(24), ...s }))

    const uniqueCount = uniqueSpeakers.length
    const existingCount = importedSpeakers.length - uniqueCount

    const batchSize = 500 // firebase allows us to commit a batch max 500 documents
    const batchedSpeakersData = chunk(uniqueSpeakers, batchSize)

    // Track speaker doc refs alongside their data so we can link attendees after insert
    const speakerDocPairs = [] // [{ docRef, speakerData }]

    const batchedPromises = batchedSpeakersData.map((speakersBatch) => {
      const batch = FirestoreDatabase.batch()
      speakersBatch.forEach((speaker) => {
        const speakerCopy = { ...speaker }
        speakerCopy?.id && delete speakerCopy['id']
        const docRef = FirestoreDatabase.collection(Collection.SPEAKERS).doc()
        batch.set(docRef, speakerCopy)
        speakerDocPairs.push({ docRef, speakerData: speakerCopy })
      })
      return batch.commit()
    })

    await Promise.all(batchedPromises)

    // Create linked attendee records for each newly inserted speaker
    const attendeeLinkPairs = await Promise.all(
      speakerDocPairs.map(async ({ docRef, speakerData }) => {
        const attendeeId = await createOrUpdateAttendeeForSpeaker(
          { ...speakerData, speakerId: docRef.id },
          conferenceId
        )
        return { docRef, attendeeId }
      })
    )

    // Batch-update speakers with their attendeeId
    const linkBatches = chunk(
      attendeeLinkPairs.filter((p) => p.attendeeId),
      batchSize
    )
    await Promise.all(
      linkBatches.map((linkBatch) => {
        const batch = FirestoreDatabase.batch()
        linkBatch.forEach(({ docRef, attendeeId }) => {
          batch.update(docRef, { attendeeId })
        })
        return batch.commit()
      })
    )

    const imagePromises = []
    for (const speakersBatch of batchedSpeakersData) {
      for (const speaker of speakersBatch) {
        const collectionName = Collection.SPEAKERS
        if (speaker?.imageName && previousConferenceId) {
          const sourcePath = `images/${previousConferenceId}/${collectionName}/${speaker?.imageName}`
          const destinationPath = `images/${conferenceId}/${collectionName}/${speaker?.imageName}`
          imagePromises.push(placeImageToDifferentPath(sourcePath, destinationPath))
        }
      }
    }
    await Promise.all(imagePromises)

    return { uniqueCount, existingCount, existingSpeakerEmails: existingSpeakerEmails || [] }
  } catch (err) {
    toast.error('Speaker import failed. Try again.')
    console.error(err)
    return { uniqueCount: 0, existingCount: 0, existingSpeakerEmails: [] }
  }
}

export const checkSpeakerExistsInTalks = (speakerId) => async (dispatch, getState) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('speakers', 'array-contains', speakerId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const fetchSpeakerById = (speakerId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const e = snapshot.data()
    dispatch(
      speakerSlice.actions.setSpeaker({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const updateSpeakerById = (speakerId, payload, conferenceId) => async (dispatch) => {
  try {
    const { email, roles } = payload
    dispatch(speakerSlice.actions.setProgress(true))

    // First, check if the email already exists
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    const matchingSnapshot = snapshot.docs.filter((doc) => doc.id !== speakerId)

    if (matchingSnapshot.length) {
      toast.error('A speaker with this email already exists.')
      return null
    }

    // Get current speaker data to compare roles
    const speakerDoc = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const currentSpeakerData = speakerDoc.data()
    const currentRoles = currentSpeakerData?.roles || []

    // Check if any roles are being removed
    const removedRoles = currentRoles.filter((role) => !roles.includes(role))

    if (removedRoles.length > 0) {
      // Check if the speaker is associated with any events/sessions/talks in the removed role
      let isAssociated = false
      let roleType = ''

      // Check in talks
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!talksSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in talks for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!talksSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      // Check in events for presenter role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!eventsSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in events for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!eventsSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      // Check in sessions for presenter role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!sessionsSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in sessions for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!sessionsSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      if (isAssociated) {
        toast.error(
          `Cannot remove ${roleType} role. This speaker is acting as a ${roleType} in Event(s), Sub-event(s), or Talk(s).`
        )
        return false
      }
    }

    // If all validations pass, update the speaker
    await FirestoreDatabase.collection(Collection.SPEAKERS)
      .doc(speakerId)
      .update({
        ...payload
      })

    // Sync linked attendee
    const updatedSpeakerDoc = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .doc(speakerId)
      .get()

    const speakerData = updatedSpeakerDoc.data()
    const existingAttendeeId = speakerData?.attendeeId
    if (existingAttendeeId) {
      await createOrUpdateAttendeeForSpeaker(speakerData, conferenceId, existingAttendeeId)
    } else {
      const attendeeId = await createOrUpdateAttendeeForSpeaker(
        { ...speakerData, speakerId },
        conferenceId
      )
      if (attendeeId) {
        await FirestoreDatabase.collection(Collection.SPEAKERS)
          .doc(speakerId)
          .update({ attendeeId })
      }
    }

    return speakerId
  } catch (err) {
    dispatch(speakerSlice.actions.setProgress(false))
    throw err
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const updateSpeakerBio = async (speakerId, bio, originalBio) => {
  await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).update({
    bio,
    originalBio
  })
}

export const deleteSpeakerById = (conferenceId, speakerId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const e = snapshot.data()
    if (e.imageName) {
      await storageRef.child(`${conferenceId}/${Collection.SPEAKERS}/${e.imageName}`).delete()
    }
    await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).delete()
    return true
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const getSpeakers = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SPEAKERS)
    .where('conferenceId', '==', conferenceId)
    .onSnapshot((documents) => {
      const speakers = []
      documents.forEach((doc) => {
        speakers.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(speakers)
    })
}

export const getSpeakersAvailablePolls = async (conferenceId, selectedSpeakersIds) => {
  try {
    const batchSize = 10
    const batchedSpeakerIds = chunk(selectedSpeakersIds, batchSize)

    const batchSnaps = await Promise.all(
      batchedSpeakerIds.map((batchSpeakerIds) =>
        FirestoreDatabase.collection(Collection.POLLS)
          .where('conferenceId', '==', conferenceId)
          .where('speakerId', 'in', batchSpeakerIds)
          .get()
      )
    )

    const pollsData = []
    batchSnaps.forEach((pollsSnap) => {
      pollsSnap.docs.forEach((doc) => {
        pollsData.push({ id: doc?.id, ...doc.data() })
      })
    })
    return pollsData
  } catch (err) {
    console.error(err)
  }
}

export const getModeratorsAndPresenters = async (conferenceId) => {
  const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
    .where('conferenceId', '==', conferenceId)
    .get()

  const speakers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  const moderators = speakers?.filter((speaker) => speaker?.roles?.includes(SpeakerRoles.MODERATOR))
  const presenters = speakers?.filter((speaker) => speaker?.roles?.includes(SpeakerRoles.PRESENTER))

  return { moderators, presenters }
}

export const resetSpeaker = () => (dispatch) => {
  dispatch(speakerSlice.actions.setSpeaker(null))
}

export const updateAndReinviteSpeaker = async (payload, conferenceId, speakerId, emailChanged) => {
  try {
    const { email, firstName, lastName, roles } = payload

    // Check if the new email already exists (skip if email not changed)
    if (emailChanged) {
      const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
        .where('email', '==', email)
        .where('conferenceId', '==', conferenceId)
        .get()

      if (!snapshot.empty) {
        toast.error('A speaker with this email already exists.')
        return null
      }
    }

    // Get current speaker data to compare roles
    const speakerDoc = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const currentSpeakerData = speakerDoc.data()
    const currentRoles = currentSpeakerData?.roles || []

    // Check if any roles are being removed
    const removedRoles = currentRoles.filter((role) => !roles.includes(role))

    if (removedRoles.length > 0) {
      // Check if the speaker is associated with any events/sessions/talks in the removed role
      let isAssociated = false
      let roleType = ''

      // Check in talks
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!talksSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in talks for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!talksSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      // Check in events for presenter role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!eventsSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in events for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const eventsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!eventsSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      // Check in sessions for presenter role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.PRESENTER)) {
        const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
          .where('conferenceId', '==', conferenceId)
          .where('speakers', 'array-contains', speakerId)
          .get()

        if (!sessionsSnapshot.empty) {
          isAssociated = true
          roleType = 'presenter'
        }
      }

      // Check in sessions for moderator role
      if (!isAssociated && removedRoles.includes(SpeakerRoles.MODERATOR)) {
        const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
          .where('conferenceId', '==', conferenceId)
          .where('moderators', 'array-contains', speakerId)
          .get()

        if (!sessionsSnapshot.empty) {
          isAssociated = true
          roleType = 'moderator'
        }
      }

      if (isAssociated) {
        toast.error(
          `Cannot remove ${roleType} role. This speaker is acting as a ${roleType} in Event(s), Sub-event(s), or Talk(s).`
        )
        return false
      }
    }

    // Prepare update data
    const updateData = {
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`,
      email,
      roles,
      updatedAt: moment().utc().valueOf()
    }

    // Generate new token if email changed
    if (emailChanged) {
      updateData.token = nanoid()
      updateData.status = SubjectStatus.INVITE_NOT_SENT
    }

    // Update the speaker document
    await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).update(updateData)

    // Sync linked attendee
    const speakerDocSnap = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .doc(speakerId)
      .get()
    const existingAttendeeId = speakerDocSnap.data()?.attendeeId
    if (existingAttendeeId) {
      await createOrUpdateAttendeeForSpeaker(
        { firstName, lastName, email, roles },
        conferenceId,
        existingAttendeeId
      )
    } else {
      const attendeeId = await createOrUpdateAttendeeForSpeaker(
        { firstName, lastName, email, roles, speakerId },
        conferenceId
      )
      if (attendeeId) {
        await FirestoreDatabase.collection(Collection.SPEAKERS)
          .doc(speakerId)
          .update({ attendeeId })
      }
    }

    // Send invitation email if email changed
    if (emailChanged) {
      try {
        await cloudFunctionsInstance.post('/inviteOrRemindSpeakersViaEmail', {
          speakerIds: [speakerId],
          conferenceId
        })
        toast.success('Speaker updated and new invitation sent.')
      } catch (err) {
        const errorMessage =
          err?.response?.data?.msg || 'Failed to send email, Please try again later'
        toast.error(errorMessage)
      }
    } else {
      toast.success('Speaker updated.')
    }

    return true
  } catch (err) {
    toast.error('Could not update speaker.')
    console.error(err)
    return false
  }
}

export default speakerSlice
